import React, { useEffect, useMemo } from "react";
import { Button, Input, Select } from "@pd/pd-ui-kit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useUrlFilters } from "~/features/filter";
import { useSubdivisionOptions } from "~/features/filter";
import { filterOptionByLabel } from "~/shared/lib/filterHelpers";
import { usersFilterFields } from "~/widgets/Filter/UsersFilter/model/fields";
import { useDealerOptions } from "~/widgets/Filter/UsersFilter/model/useDealerOptions";
import { usePermissionOptions } from "~/widgets/Filter/UsersFilter/model/usePermissionOptions";
import { useRoleOptions } from "~/widgets/Filter/UsersFilter/model/useRoleOptions";
import { useStatusOptions } from "~/widgets/Filter/UsersFilter/model/useStatusOptions";

import local from "./UsersFilter.less";

interface UsersFilterForm {
  id: string;
  fullName: string;
  searchText: string;
  position: string;
  email: string;
  phone: string;
  dealerIds: string[];
  status: string;
  roleNames: string[];
  permissionIds: string[];
  subdivisionIds: string[];
}

const EMPTY_FORM: UsersFilterForm = {
  id: "",
  fullName: "",
  searchText: "",
  position: "",
  email: "",
  phone: "",
  dealerIds: [],
  status: "",
  roleNames: [],
  permissionIds: [],
  subdivisionIds: [],
};

const toStr = (value: unknown): string => (value as string) || "";
const toStrArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(String) : [];

const popupScrollHandler =
  (onScrollBottom?: () => void) => (event: React.UIEvent<HTMLDivElement>) => {
    if (!onScrollBottom) return;
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
      onScrollBottom();
    }
  };

export const UsersFilter = () => {
  const { t } = useTranslation();
  const fields = useMemo(() => usersFilterFields(t), [t]);
  const { values, applyFilters, resetFilters } = useUrlFilters(fields);

  const {
    options: dealerOptions,
    loading: dealerLoading,
    onScrollBottom: onScrollBottomDealers,
    searchOptions: searchDealers,
  } = useDealerOptions();
  const { options: roleOptions, loading: roleLoading } = useRoleOptions();
  const { options: permissionOptions, loading: permissionLoading } =
    usePermissionOptions();
  const { options: statusOptions } = useStatusOptions();
  const {
    options: subdivisionOptions,
    loading: subdivisionLoading,
    onScrollBottom: onScrollBottomSubdivisions,
    searchOptions: searchSubdivisions,
  } = useSubdivisionOptions();

  const statusFilterOptions = useMemo(
    () => [
      { value: "", label: t("users.notSelected") },
      ...statusOptions.map((option) => ({
        value: String(option.value),
        label: option.label,
      })),
    ],
    [statusOptions, t],
  );

  const { control, handleSubmit, reset } = useForm<UsersFilterForm>({
    defaultValues: {
      ...EMPTY_FORM,
      id: toStr(values.id),
      fullName: toStr(values.fullName),
      searchText: toStr(values.searchText),
      position: toStr(values.position),
      email: toStr(values.email),
      phone: toStr(values.phone),
      dealerIds: toStrArray(values.dealerIds),
      status: toStr(values.status),
      roleNames: toStrArray(values.roleNames),
      permissionIds: toStrArray(values.permissionIds),
      subdivisionIds: toStrArray(values.subdivisionIds),
    },
  });

  useEffect(() => {
    reset({
      ...EMPTY_FORM,
      id: toStr(values.id),
      fullName: toStr(values.fullName),
      searchText: toStr(values.searchText),
      position: toStr(values.position),
      email: toStr(values.email),
      phone: toStr(values.phone),
      dealerIds: toStrArray(values.dealerIds),
      status: toStr(values.status),
      roleNames: toStrArray(values.roleNames),
      permissionIds: toStrArray(values.permissionIds),
      subdivisionIds: toStrArray(values.subdivisionIds),
    });
  }, [values, reset]);

  const onSubmit = (form: UsersFilterForm) => {
    applyFilters({ ...form });
  };

  const onReset = () => {
    reset(EMPTY_FORM);
    resetFilters();
  };

  return (
    <form className={local.formFilter} onSubmit={handleSubmit(onSubmit)}>
      <div className={local.row}>
        <div className={local.field}>
          <span className={local.label}>{t("users.searchByName")}</span>
          <Controller
            name="fullName"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("users.enter")}
              />
            )}
          />
        </div>
        <div className={local.field}>
          <span className={local.label}>{t("users.id")}</span>
          <Controller
            name="id"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("users.enter")}
              />
            )}
          />
        </div>
        <div className={local.field}>
          <span className={local.label}>{t("users.login")}</span>
          <Controller
            name="searchText"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("users.enter")}
              />
            )}
          />
        </div>
        <div className={local.field}>
          <span className={local.label}>{t("users.position")}</span>
          <Controller
            name="position"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("users.enter")}
              />
            )}
          />
        </div>
        <div className={local.field}>
          <span className={local.label}>{t("users.email")}</span>
          <Controller
            name="email"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("users.enter")}
              />
            )}
          />
        </div>
      </div>

      <div className={local.row}>
        <div className={local.field}>
          <span className={local.label}>{t("users.phone")}</span>
          <Controller
            name="phone"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("users.enter")}
              />
            )}
          />
        </div>
        <div className={local.field}>
          <span className={local.label}>{t("users.dealer")}</span>
          <Controller
            name="dealerIds"
            control={control}
            render={({ onChange, value }) => (
              <Select
                allowClear
                showSearch
                mode="multiple"
                value={value.length ? value : undefined}
                options={dealerOptions}
                loading={dealerLoading}
                placeholder={t("users.notSelected")}
                onChange={(next) =>
                  onChange(Array.isArray(next) ? next.map(String) : [])
                }
                onPopupScroll={popupScrollHandler(onScrollBottomDealers)}
                onSearch={searchDealers}
                filterOption={false}
              />
            )}
          />
        </div>
        <div className={local.field}>
          <span className={local.label}>{t("users.status")}</span>
          <Controller
            name="status"
            control={control}
            render={({ onChange, value }) => (
              <Select
                value={value || undefined}
                placeholder={t("users.notSelected")}
                options={statusFilterOptions}
                onChange={(next) => onChange(next ?? "")}
              />
            )}
          />
        </div>
        <div className={local.field}>
          <span className={local.label}>{t("users.role")}</span>
          <Controller
            name="roleNames"
            control={control}
            render={({ onChange, value }) => (
              <Select
                allowClear
                showSearch
                mode="multiple"
                value={value.length ? value : undefined}
                options={roleOptions}
                loading={roleLoading}
                placeholder={t("users.notSelected")}
                onChange={(next) =>
                  onChange(Array.isArray(next) ? next.map(String) : [])
                }
                filterOption={filterOptionByLabel}
              />
            )}
          />
        </div>
        <div className={local.field}>
          <span className={local.label}>{t("users.permissions")}</span>
          <Controller
            name="permissionIds"
            control={control}
            render={({ onChange, value }) => (
              <Select
                allowClear
                showSearch
                mode="multiple"
                value={value.length ? value : undefined}
                options={permissionOptions}
                loading={permissionLoading}
                placeholder={t("users.notSelected")}
                onChange={(next) =>
                  onChange(Array.isArray(next) ? next.map(String) : [])
                }
                filterOption={filterOptionByLabel}
              />
            )}
          />
        </div>
        <div className={local.field}>
          <span className={local.label}>{t("users.subdivision")}</span>
          <Controller
            name="subdivisionIds"
            control={control}
            render={({ onChange, value }) => (
              <Select
                allowClear
                showSearch
                mode="multiple"
                value={value.length ? value : undefined}
                options={subdivisionOptions}
                loading={subdivisionLoading}
                placeholder={t("users.notSelected")}
                onChange={(next) =>
                  onChange(Array.isArray(next) ? next.map(String) : [])
                }
                onPopupScroll={popupScrollHandler(onScrollBottomSubdivisions)}
                onSearch={searchSubdivisions}
                filterOption={false}
              />
            )}
          />
        </div>
      </div>

      <div className={local.actions}>
        <Button
          htmlType="button"
          color="secondary"
          variant="text"
          bold={false}
          fontSize="12"
          size="medium"
          onClick={onReset}
        >
          {t("ui.filter_reset")}
        </Button>
        <Button
          htmlType="submit"
          bold
          color="secondary"
          fontSize="12"
          size="medium"
        >
          {t("ui.filter_show")}
        </Button>
      </div>
    </form>
  );
};
